import DashboardService from "app/components/dashboard/services/DashboardService";
import type { IWidget } from "app/components/widget/widget-types/Widget";
import { WidgetState } from "app/components/widget/widget-types/Widget";
import {
    CURRENT_DASHBOARD_CHANGE_TITLE,
    CURRENT_DASHBOARD_CHECK_WIDGET_LOADED,
    CURRENT_DASHBOARD_INIT_DASHBOARD,
    CURRENT_DASHBOARD_IS_LOADING,
    CURRENT_DASHBOARD_RESET,
    CURRENT_DASHBOARD_TOGGLE_WIDGET_ENLARGE,
    CURRENT_DASHBOARD_WIDGET_ADDED,
    CURRENT_DASHBOARD_WIDGET_ENLARGE_STATE_CHANGED,
    CURRENT_DASHBOARD_WIDGET_REMOVED,
    CURRENT_DASHBOARD_WIDGET_UPDATED,
} from "../actions/CurrentDashboardActionTypes";

export const selectIsDashboardLoading = (state: { customDashboard: { currentDashboard: ICurrentDashboardState } }) =>
    state.customDashboard.currentDashboard.isLoading;

interface ICurrentDashboardState {
    widgets: { [key: string]: IWidget<any> };
    widgetsEnlargedState: { [key: string]: boolean };
    widgetsLoaded: boolean;
    lastWidgetUpdated: { id: string | null; counter: number };
    dashboardTitle: string | null;
    dashboardId: string | null;
    isLoading: boolean;
}

const initialState: ICurrentDashboardState = {
    widgets: {},
    widgetsEnlargedState: {},
    widgetsLoaded: false,
    lastWidgetUpdated: {
        id: null,
        counter: 0,
    },
    dashboardTitle: null,
    dashboardId: null,
    isLoading: false,
};

export default function (state: ICurrentDashboardState = initialState, action: { type: string; [key: string]: any }) {
    switch (action.type) {
        case CURRENT_DASHBOARD_INIT_DASHBOARD:
            return {
                ...state,
                dashboardId: action.dashboardId,
                dashboardTitle: action.dashboardTitle,
                widgets: (action.widgets as IWidget<any>[]).reduce(
                    (acc: Record<string, IWidget<any>>, cur: IWidget<any>) => ({ ...acc, [cur.id]: { ...cur } }),
                    {},
                ),
                widgetsEnlargedState: (action.widgets as IWidget<any>[]).reduce(
                    (acc: Record<string, boolean>, cur: IWidget<any>) => {
                        return { ...acc, [cur.id]: !(cur.pos?.sizeY === 1) };
                    },
                    {},
                ),
                widgetsLoaded: (action.widgets as IWidget<any>[]).every(
                    (item: IWidget<any>) =>
                        item.widgetState === WidgetState.LOADED ||
                        item.widgetState === WidgetState.ERROR ||
                        item.widgetState === WidgetState.SOFT_LIMIT_REACHED ||
                        item.widgetState === WidgetState.ASSET_NOT_ACCESSIBLE,
                ),
                lastWidgetUpdated: {
                    id: null,
                    counter: 0,
                },
                toggleEnlargeWidget: {
                    id: null,
                    counter: 0,
                },
            };
        case CURRENT_DASHBOARD_CHECK_WIDGET_LOADED:
            const loaded = DashboardService.dashboard.widgets.every(
                (item: IWidget<any>) =>
                    item.widgetState === WidgetState.LOADED ||
                    item.widgetState === WidgetState.ERROR ||
                    item.widgetState === WidgetState.SOFT_LIMIT_REACHED ||
                    item.widgetState === WidgetState.ASSET_NOT_ACCESSIBLE,
            );
            if (loaded === state.widgetsLoaded) {
                return state;
            }
            return {
                ...state,
                widgetsLoaded: loaded,
            };
        case CURRENT_DASHBOARD_WIDGET_ADDED:
            return {
                ...state,
                widgets: { ...state.widgets, [action.widget.id]: { ...action.widget } },
            };
        case CURRENT_DASHBOARD_WIDGET_REMOVED:
            const { [action.widget.id]: _removed, ...rest } = state.widgets;
            return {
                ...state,
                widgets: rest,
            };
        case CURRENT_DASHBOARD_WIDGET_UPDATED:
            return {
                ...state,
                widgets: {
                    ...state.widgets,
                    [action.widget.id]: { ...action.widget },
                },
                lastWidgetUpdated: {
                    id: action.widget.id,
                    counter: state.lastWidgetUpdated.counter + 1,
                },
            };
        case CURRENT_DASHBOARD_CHANGE_TITLE:
            if (action.dashboardId !== state.dashboardId) {
                return state;
            }
            return {
                ...state,
                dashboardTitle: action.dashboardTitle,
            };
        case CURRENT_DASHBOARD_WIDGET_ENLARGE_STATE_CHANGED:
            if (state.dashboardId !== action.widget?.dashboardId) {
                return state;
            }
            //SET the new enlarge state for widget
            return {
                ...state,
                widgetsEnlargedState: {
                    ...state.widgetsEnlargedState,
                    [action.widget.id]: action.enlarged,
                },
            };
        case CURRENT_DASHBOARD_TOGGLE_WIDGET_ENLARGE:
            if (state.dashboardId !== action.widget?.dashboardId) {
                return state;
            }
            //Signal toggle enlarge state
            return {
                ...state,
                widgetsEnlargedState: {
                    ...state.widgetsEnlargedState,
                    [action.widget.id]: !state.widgetsEnlargedState[action.widget.id],
                },
            };
        case CURRENT_DASHBOARD_IS_LOADING:
            return {
                ...state,
                isLoading: action.isLoading,
            };
        case CURRENT_DASHBOARD_RESET:
            return initialState;
        default:
            // need this for default case
            return state;
    }
}
